import { useState, useEffect, useCallback } from "react";
import {
  listConnections,
  createConnection,
  updateConnection,
  deleteConnection,
  testConnection,
  type ConnectionDto,
} from "./api/client";
import { toast } from "@/components/Toaster";
import { ConfirmModal } from "@/components/ConfirmModal";
import styles from "./ConnectionsPage.module.css";
import "../admin.css";

// ── Connection types ───────────────────────────────────────────────────────────

type ConnectionType = "postgres" | "sharepoint";

/** Mirrors the API `PgSslMode` (apps/api pg-ssl-option). Web can't import across
 *  the app boundary, so this is the local source of truth for the SQL form. */
type SslMode = "disable" | "require" | "no-verify";

// ── Postgres form state ────────────────────────────────────────────────────────

interface PostgresFormState {
  type: "postgres";
  name: string;
  host: string;
  port: string;
  database: string;
  user: string;
  password: string;
  sslMode: SslMode;
  caCert: string;
  schema: string;
}

// ── SharePoint form state ──────────────────────────────────────────────────────

interface SharepointFormState {
  type: "sharepoint";
  name: string;
  azureTenantId: string;
  clientId: string;
  clientSecret: string;
  siteHostname: string;
  sitePath: string;
}

type FormState = PostgresFormState | SharepointFormState;

const defaultPostgresForm = (): PostgresFormState => ({
  type: "postgres",
  name: "",
  host: "",
  port: "5432",
  database: "",
  user: "",
  password: "",
  sslMode: "require",
  caCert: "",
  schema: "",
});

const defaultSharepointForm = (): SharepointFormState => ({
  type: "sharepoint",
  name: "",
  azureTenantId: "",
  clientId: "",
  clientSecret: "",
  siteHostname: "",
  sitePath: "",
});

function formFromDto(dto: ConnectionDto): FormState {
  if (dto.type === "sharepoint") {
    const cfg = dto.config as {
      azureTenantId?: string;
      clientId?: string;
      siteHostname?: string;
      sitePath?: string;
    };
    return {
      type: "sharepoint",
      name: dto.name,
      azureTenantId: String(cfg.azureTenantId ?? ""),
      clientId: String(cfg.clientId ?? ""),
      clientSecret: "", // NEVER pre-fill — write-only
      siteHostname: String(cfg.siteHostname ?? ""),
      sitePath: String(cfg.sitePath ?? ""),
    };
  }

  // postgres (default)
  const cfg = dto.config as {
    host?: string;
    port?: number;
    database?: string;
    user?: string;
    ssl?: boolean;
    sslMode?: SslMode;
    caCert?: string;
    schema?: string;
  };
  return {
    type: "postgres",
    name: dto.name,
    host: String(cfg.host ?? ""),
    port: String(cfg.port ?? "5432"),
    database: String(cfg.database ?? ""),
    user: String(cfg.user ?? ""),
    password: "", // NEVER pre-fill — write-only
    sslMode: (cfg.sslMode ?? (cfg.ssl ? "require" : "disable")) as SslMode,
    caCert: String(cfg.caCert ?? ""),
    schema: String(cfg.schema ?? ""),
  };
}

// ── Connection drawer ──────────────────────────────────────────────────────────

interface ConnectionDrawerProps {
  editing: ConnectionDto | null; // null = create mode
  onClose: () => void;
  onSaved: () => void;
}

function ConnectionDrawer({ editing, onClose, onSaved }: ConnectionDrawerProps) {
  const [form, setForm] = useState<FormState>(
    editing ? formFromDto(editing) : defaultPostgresForm(),
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Close on Escape
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  function handleTypeChange(newType: ConnectionType) {
    if (newType === "sharepoint") {
      setForm({ ...defaultSharepointForm(), name: form.name });
    } else {
      setForm({ ...defaultPostgresForm(), name: form.name });
    }
  }

  function setField(field: string, value: string | boolean) {
    setForm((prev) => ({ ...prev, [field]: value } as FormState));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!form.name.trim()) { setError("Name is required."); return; }

    if (form.type === "sharepoint") {
      if (!form.azureTenantId.trim()) { setError("Azure Tenant ID is required."); return; }
      if (!form.clientId.trim()) { setError("Client ID is required."); return; }
      if (!editing && !form.clientSecret) { setError("Client Secret is required for new connections."); return; }
      if (!form.siteHostname.trim()) { setError("Site Hostname is required."); return; }
      if (!form.sitePath.trim()) { setError("Site Path is required."); return; }
    } else {
      const portNum = parseInt(form.port, 10);
      if (!form.host.trim()) { setError("Host is required."); return; }
      if (isNaN(portNum)) { setError("Port must be a number."); return; }
      if (!form.database.trim()) { setError("Database is required."); return; }
      if (!form.user.trim()) { setError("User is required."); return; }
      if (!editing && !form.password) { setError("Password is required for new connections."); return; }
    }

    // Parse port once for postgres (validation above already confirmed it's a valid number)
    const portNum = form.type === "postgres" ? parseInt(form.port, 10) : 0;

    try {
      setIsSubmitting(true);

      if (form.type === "sharepoint") {
        const config = {
          azureTenantId: form.azureTenantId.trim(),
          clientId: form.clientId.trim(),
          siteHostname: form.siteHostname.trim(),
          sitePath: form.sitePath.trim(),
        };

        if (editing) {
          await updateConnection(editing.id, {
            name: form.name.trim(),
            config,
            ...(form.clientSecret ? { clientSecret: form.clientSecret } : {}),
          });
        } else {
          await createConnection({
            name: form.name.trim(),
            type: "sharepoint",
            config,
            clientSecret: form.clientSecret,
          });
        }
      } else {
        // postgres
        const config = {
          host: form.host.trim(),
          port: portNum,
          database: form.database.trim(),
          user: form.user.trim(),
          sslMode: form.sslMode,
          ...(form.sslMode === "require" && form.caCert.trim() ? { caCert: form.caCert.trim() } : {}),
          ...(form.schema.trim() ? { schema: form.schema.trim() } : {}),
        };

        if (editing) {
          await updateConnection(editing.id, {
            name: form.name.trim(),
            config,
            ...(form.password ? { password: form.password } : {}),
          });
        } else {
          await createConnection({
            name: form.name.trim(),
            type: "postgres",
            config,
            password: form.password,
          });
        }
      }

      onSaved();
    } catch (err) {
      setError((err as Error).message ?? "Failed to save connection.");
    } finally {
      setIsSubmitting(false);
    }
  }

  const isEditingSharepoint = editing?.type === "sharepoint";

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.drawer} onClick={(e) => e.stopPropagation()}>
        <h2 className={styles.drawerTitle}>
          {editing ? "Edit Connection" : "Add Connection"}
        </h2>

        <form onSubmit={handleSubmit}>
          {/* Name — common to all types */}
          <div className={styles.fieldGroup}>
            <label className={styles.label} htmlFor="conn-name">Name</label>
            <input
              id="conn-name"
              className={styles.input}
              type="text"
              value={form.name}
              onChange={(e) => setField("name", e.target.value)}
              required
              autoFocus={!editing}
            />
          </div>

          {/* Type selector — only active in create mode */}
          <div className={styles.fieldGroup}>
            <label className={styles.label} htmlFor="conn-type">Type</label>
            <select
              id="conn-type"
              className={styles.select}
              value={form.type}
              disabled={!!editing}
              onChange={(e) => handleTypeChange(e.target.value as ConnectionType)}
            >
              <option value="postgres">PostgreSQL</option>
              <option value="sharepoint">SharePoint</option>
            </select>
          </div>

          {/* ── SharePoint fields ── */}
          {form.type === "sharepoint" && (
            <>
              <div className={styles.fieldGroup}>
                <label className={styles.label} htmlFor="conn-azure-tenant-id">Azure Tenant ID</label>
                <input
                  id="conn-azure-tenant-id"
                  className={styles.input}
                  type="text"
                  value={form.azureTenantId}
                  onChange={(e) => setField("azureTenantId", e.target.value)}
                  required
                />
              </div>

              <div className={styles.fieldGroup}>
                <label className={styles.label} htmlFor="conn-client-id">Client ID</label>
                <input
                  id="conn-client-id"
                  className={styles.input}
                  type="text"
                  value={form.clientId}
                  onChange={(e) => setField("clientId", e.target.value)}
                  required
                />
              </div>

              <div className={styles.fieldGroup}>
                <label className={styles.label} htmlFor="conn-client-secret">Client Secret</label>
                <input
                  id="conn-client-secret"
                  className={styles.input}
                  type="password"
                  value={form.clientSecret}
                  onChange={(e) => setField("clientSecret", e.target.value)}
                  autoComplete="new-password"
                  placeholder={isEditingSharepoint ? "leave blank to keep current" : ""}
                />
              </div>

              <div className={styles.fieldGroup}>
                <label className={styles.label} htmlFor="conn-site-hostname">Site Hostname</label>
                <input
                  id="conn-site-hostname"
                  className={styles.input}
                  type="text"
                  value={form.siteHostname}
                  onChange={(e) => setField("siteHostname", e.target.value)}
                  placeholder="contoso.sharepoint.com"
                  required
                />
              </div>

              <div className={styles.fieldGroup}>
                <label className={styles.label} htmlFor="conn-site-path">Site Path</label>
                <input
                  id="conn-site-path"
                  className={styles.input}
                  type="text"
                  value={form.sitePath}
                  onChange={(e) => setField("sitePath", e.target.value)}
                  placeholder="/sites/Finance"
                  required
                />
              </div>
            </>
          )}

          {/* ── Postgres fields ── */}
          {form.type === "postgres" && (
            <>
              <div className={styles.fieldGroup}>
                <label className={styles.label} htmlFor="conn-host">Host</label>
                <input
                  id="conn-host"
                  className={styles.input}
                  type="text"
                  value={form.host}
                  onChange={(e) => setField("host", e.target.value)}
                  required
                />
              </div>

              <div className={styles.fieldGroup}>
                <label className={styles.label} htmlFor="conn-port">Port</label>
                <input
                  id="conn-port"
                  className={styles.input}
                  type="number"
                  value={form.port}
                  onChange={(e) => setField("port", e.target.value)}
                  required
                />
              </div>

              <div className={styles.fieldGroup}>
                <label className={styles.label} htmlFor="conn-database">Database</label>
                <input
                  id="conn-database"
                  className={styles.input}
                  type="text"
                  value={form.database}
                  onChange={(e) => setField("database", e.target.value)}
                  required
                />
              </div>

              <div className={styles.fieldGroup}>
                <label className={styles.label} htmlFor="conn-user">User</label>
                <input
                  id="conn-user"
                  className={styles.input}
                  type="text"
                  value={form.user}
                  onChange={(e) => setField("user", e.target.value)}
                  required
                />
              </div>

              <div className={styles.fieldGroup}>
                <label className={styles.label} htmlFor="conn-password">Password</label>
                <input
                  id="conn-password"
                  className={styles.input}
                  type="password"
                  value={form.password}
                  onChange={(e) => setField("password", e.target.value)}
                  autoComplete="new-password"
                  placeholder={editing ? "leave blank to keep current" : ""}
                />
              </div>

              <div className={styles.fieldGroup}>
                <label className={styles.label} htmlFor="conn-sslmode">SSL Mode</label>
                <select
                  id="conn-sslmode"
                  className={styles.input}
                  value={form.sslMode}
                  onChange={(e) => setField("sslMode", e.target.value as SslMode)}
                >
                  <option value="disable">Disable (no TLS)</option>
                  <option value="require">Require (verify certificate)</option>
                  <option value="no-verify">No-verify (encrypted, skip verification)</option>
                </select>
              </div>
              {form.sslMode === "require" && (
                <div className={styles.fieldGroup}>
                  <label className={styles.label} htmlFor="conn-cacert">
                    CA Certificate <span style={{ fontWeight: 400, textTransform: "none" }}>(optional)</span>
                  </label>
                  <textarea
                    id="conn-cacert"
                    className={styles.input}
                    rows={4}
                    placeholder="-----BEGIN CERTIFICATE----- … (paste a custom CA bundle, e.g. for RDS)"
                    value={form.caCert}
                    onChange={(e) => setField("caCert", e.target.value)}
                  />
                </div>
              )}

              <div className={styles.fieldGroup}>
                <label className={styles.label} htmlFor="conn-schema">
                  Schema <span style={{ fontWeight: 400, textTransform: "none" }}>(optional)</span>
                </label>
                <input
                  id="conn-schema"
                  className={styles.input}
                  type="text"
                  value={form.schema}
                  onChange={(e) => setField("schema", e.target.value)}
                />
              </div>
            </>
          )}

          {error && <div className={styles.drawerError}>{error}</div>}

          <div className={styles.drawerFooter}>
            <button
              type="button"
              className={styles.cancelBtn}
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.saveBtn}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Host/database cell by type ─────────────────────────────────────────────────

function ConnectionHostCell({ conn }: { conn: ConnectionDto }) {
  if (conn.type === "sharepoint") {
    const cfg = conn.config as { siteHostname?: string; sitePath?: string };
    return <>{String(cfg.siteHostname ?? "")}</>;
  }
  // postgres
  const cfg = conn.config as { host?: string; port?: number; database?: string };
  return <>{String(cfg.host ?? "")}:{String(cfg.port ?? "")} /{String(cfg.database ?? "")}</>;
}

// ── Page ────────────────────────────────────────────────────────────────────────

type TestResult = { ok: boolean; error?: string };
type TestResultMap = Record<string, TestResult>;

export function ConnectionsPage() {
  const [connections, setConnections] = useState<ConnectionDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingConnection, setEditingConnection] = useState<ConnectionDto | null>(null);

  // Delete confirm state
  const [deletingConnection, setDeletingConnection] = useState<ConnectionDto | null>(null);

  // Per-row test results
  const [testResults, setTestResults] = useState<TestResultMap>({});

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      setLoadError(null);
      const data = await listConnections();
      setConnections(data);
      setTestResults({});
    } catch (err) {
      setLoadError((err as Error).message ?? "Failed to load connections.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  function openCreate() {
    setEditingConnection(null);
    setDrawerOpen(true);
  }

  function openEdit(conn: ConnectionDto) {
    setEditingConnection(conn);
    setDrawerOpen(true);
  }

  function closeDrawer() {
    setDrawerOpen(false);
    setEditingConnection(null);
  }

  async function handleSaved() {
    closeDrawer();
    await load();
  }

  async function handleTest(id: string) {
    try {
      const result = await testConnection(id);
      setTestResults((prev) => ({ ...prev, [id]: result }));
    } catch (err) {
      setTestResults((prev) => ({
        ...prev,
        [id]: { ok: false, error: (err as Error).message ?? "Test failed." },
      }));
    }
  }

  async function handleDeleteConfirmed() {
    if (!deletingConnection) return;
    try {
      await deleteConnection(deletingConnection.id);
      await load();
    } catch (err) {
      toast.error((err as Error).message ?? "Failed to delete connection.");
    } finally {
      setDeletingConnection(null);
    }
  }

  return (
    <div className="admin-page">
      <div className={styles.header}>
        <h1 className={styles.title}>Connections</h1>
        <button
          type="button"
          className={styles.addBtn}
          onClick={openCreate}
        >
          + Add Connection
        </button>
      </div>

      {isLoading && (
        <div className="admin-loading">
          <div className="loading-spinner" />
        </div>
      )}

      {loadError && (
        <div className="admin-error">Error: {loadError}</div>
      )}

      {!isLoading && !loadError && connections.length === 0 && (
        <div className={styles.empty}>
          No connections yet. Add a database connection to enable SQL exports.
        </div>
      )}

      {!isLoading && !loadError && connections.length > 0 && (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Host / Database</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {connections.map((conn) => {
              const testResult = testResults[conn.id];
              return (
                <tr key={conn.id}>
                  <td>{conn.name}</td>
                  <td>{conn.type}</td>
                  <td>
                    <ConnectionHostCell conn={conn} />
                  </td>
                  <td>
                    {conn.isActive ? (
                      <span className={styles.activeBadge}>Active</span>
                    ) : (
                      <span className={styles.inactiveBadge}>Inactive</span>
                    )}
                  </td>
                  <td>
                    <div className={styles.actions}>
                      <button
                        type="button"
                        className={styles.actionBtn}
                        onClick={() => void handleTest(conn.id)}
                      >
                        Test
                      </button>
                      {testResult && (
                        testResult.ok ? (
                          <span className={styles.testOk}>OK</span>
                        ) : (
                          <span className={styles.testErr}>
                            {testResult.error ?? "Failed"}
                          </span>
                        )
                      )}
                      <button
                        type="button"
                        className={styles.actionBtn}
                        onClick={() => openEdit(conn)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className={`${styles.actionBtn} ${styles.actionBtnDanger}`}
                        onClick={() => setDeletingConnection(conn)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {drawerOpen && (
        <ConnectionDrawer
          editing={editingConnection}
          onClose={closeDrawer}
          onSaved={() => void handleSaved()}
        />
      )}

      <ConfirmModal
        open={!!deletingConnection}
        title="Delete connection?"
        description={
          deletingConnection
            ? `"${deletingConnection.name}" will be permanently removed. This cannot be undone.`
            : undefined
        }
        confirmLabel="Delete"
        destructive
        onConfirm={() => void handleDeleteConfirmed()}
        onCancel={() => setDeletingConnection(null)}
      />
    </div>
  );
}
